import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/lib/translations";

const STORAGE_KEY = "luxenova.lang";
const SUPPORTED = ["en", "pl"];
const DEFAULT = "en";

const I18nContext = createContext({
    lang: DEFAULT,
    setLang: () => {},
    t: (k) => k,
    supported: SUPPORTED,
});

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        if (typeof window === "undefined") return DEFAULT;
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved && SUPPORTED.includes(saved)) return saved;
        const nav = (window.navigator?.language || "en").slice(0, 2).toLowerCase();
        return SUPPORTED.includes(nav) ? nav : DEFAULT;
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, lang);
        } catch (_) {
            /* ignore storage failures (private mode / disabled cookies) */
        }
        document.documentElement.setAttribute("lang", lang);
    }, [lang]);

    const t = useMemo(() => {
        const dict = translations[lang] || translations[DEFAULT];
        const fallback = translations[DEFAULT];
        return (key) => {
            if (key == null) return "";
            const parts = key.split(".");
            let v = dict;
            for (const p of parts) v = v?.[p];
            if (v == null) {
                let fb = fallback;
                for (const p of parts) fb = fb?.[p];
                v = fb;
            }
            return v ?? key;
        };
    }, [lang]);

    const setLang = (l) => {
        if (SUPPORTED.includes(l)) setLangState(l);
    };

    return (
        <I18nContext.Provider value={{ lang, setLang, t, supported: SUPPORTED }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    return useContext(I18nContext);
}
